import {
  Invitation,
  Inviter,
  Registerer,
  RegistererState,
  Session,
  SessionState,
  URI,
  UserAgent,
  UserAgentState,
} from "sip.js";
import { createCallLog, type CallLogDirection } from "@/services/api/callLogs";
import { queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/constants/queryKeys";

export type SipUserInfo = {
  webphone_ast_host: string;
  sip_username: string;
  sip_password: string;
  prefix?: string;
  webphone_wss_url?: string;
  ice_servers?: Array<{ urls: string | string[]; username?: string; credential?: string }>;
  auto_pickup?: boolean;
};

type CallStateCb = (
  event: string,
  direction: number,
  peername: string,
  peerdisplayname: string,
  line: string,
) => void;

type LineSession = {
  session: Session;
  direction: number;
  peer: string;
  displayName: string;
  startedAt: number;
  answeredAt: number | null;
  clientId: string;
};

const HISTORY_KEY = "papaya_pjsip_call_history";

const defaultIce = [{ urls: "stun:stun.l.google.com:19302" }];

function playRemoteAudio(session: Session) {
  const sdh = session.sessionDescriptionHandler as {
    peerConnection?: RTCPeerConnection;
  } | null;
  const pc = sdh?.peerConnection;
  if (!pc) return;

  let el = document.getElementById("papaya-remote-audio") as HTMLAudioElement | null;
  if (!el) {
    el = document.createElement("audio");
    el.id = "papaya-remote-audio";
    el.autoplay = true;
    el.setAttribute("playsinline", "true");
    el.style.display = "none";
    document.body.appendChild(el);
  }

  const remote = new MediaStream();
  pc.getReceivers().forEach((receiver) => {
    if (receiver.track) remote.addTrack(receiver.track);
  });
  el.srcObject = remote;
  void el.play().catch(() => undefined);

  pc.addEventListener("track", (event) => {
    if (event.track) remote.addTrack(event.track);
    el!.srcObject = remote;
    void el!.play().catch(() => undefined);
  });
}

function holdModifier(description: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
  if (!description.sdp) return Promise.resolve(description);
  const sdp = description.sdp
    .replace(/a=sendrecv/g, "a=sendonly")
    .replace(/a=recvonly/g, "a=inactive");
  return Promise.resolve({ ...description, sdp });
}

function unholdModifier(
  description: RTCSessionDescriptionInit,
): Promise<RTCSessionDescriptionInit> {
  if (!description.sdp) return Promise.resolve(description);
  const sdp = description.sdp
    .replace(/a=sendonly/g, "a=sendrecv")
    .replace(/a=inactive/g, "a=recvonly");
  return Promise.resolve({ ...description, sdp });
}

class SipEngine {
  params: Record<string, string> = {};
  private userAgent: UserAgent | null = null;
  private registerer: Registerer | null = null;
  private lines = new Map<number, LineSession>();
  private activeLine = 1;
  private nextLineId = 1;
  private muteState = false;
  private regState = "";
  private autoPickup = false;

  onAppStateChangeCb: ((state: string) => void) | null = null;
  onRegStateChangeCb: ((state: string) => void) | null = null;
  onCallStateChangeCb: CallStateCb | null = null;

  setparameter(key: string, value: string | number | boolean) {
    this.params[key] = String(value);
  }

  getparameter(key: string) {
    return this.params[key];
  }

  private emitApp(state: string) {
    this.onAppStateChangeCb?.(state);
  }

  private emitReg(state: string) {
    this.regState = state;
    this.onRegStateChangeCb?.(state);
  }

  private emitCall(
    event: string,
    direction: number,
    peer: string,
    display: string,
    line: number,
  ) {
    this.onCallStateChangeCb?.(event, direction, peer, display, String(line));
  }

  private wssUrl() {
    if (this.params.webphone_wss_url) return this.params.webphone_wss_url;
    const host = this.params.serveraddress || this.params.webphone_ast_host;
    if (!host) return "";
    if (host.startsWith("wss://") || host.startsWith("ws://")) return host;
    // Gateway TLS :443 → Asterisk HTTP :8088 (do not use public :8089).
    return `wss://${host}/asterisk/ws`;
  }

  private domain() {
    return this.params.serveraddress || this.params.webphone_ast_host || "";
  }

  private iceServers() {
    try {
      if (this.params.ice_servers) {
        const parsed = JSON.parse(this.params.ice_servers);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch {
      /* ignore */
    }
    return defaultIce;
  }

  async start() {
    const username = this.params.username;
    const password = this.params.password;
    const domain = this.domain();
    const server = this.wssUrl();
    if (!username || !password || !domain || !server) {
      console.error("SipEngine: missing username/password/domain/wss url");
      this.emitApp("unregistered");
      return;
    }

    this.autoPickup = this.params.autoaccept === "1" || this.params.autopickup === "1";
    this.emitApp("starting");
    await this.stopInternal(false);

    const uri = UserAgent.makeURI(`sip:${username}@${domain}`);
    if (!uri) {
      console.error("SipEngine: invalid SIP URI");
      return;
    }

    this.userAgent = new UserAgent({
      uri,
      authorizationUsername: username,
      authorizationPassword: password,
      transportOptions: { server },
      sessionDescriptionHandlerFactoryOptions: {
        iceGatheringTimeout: 5000,
        peerConnectionConfiguration: {
          iceServers: this.iceServers(),
        },
        constraints: { audio: true, video: false },
      } as never,
      delegate: {
        onInvite: (invitation) => this.handleInvite(invitation),
      },
    });

    this.userAgent.delegate = {
      ...this.userAgent.delegate,
      onInvite: (invitation) => this.handleInvite(invitation),
    };

    try {
      await this.userAgent.start();
      this.emitApp("started");
    } catch (err) {
      console.error("SipEngine start failed", err);
      this.emitApp("stopped");
    }
  }

  async register() {
    if (!this.userAgent) {
      await this.start();
    }
    if (!this.userAgent || this.userAgent.state !== UserAgentState.Started) return;
    this.emitApp("registering");
    this.registerer = new Registerer(this.userAgent, { expires: 3600 });
    this.registerer.stateChange.addListener((state) => {
      if (state === RegistererState.Registered) this.emitReg("registered");
      else if (state === RegistererState.Unregistered) this.emitReg("unregistered");
      else if (state === RegistererState.Terminated) this.emitReg("unregistered");
    });
    try {
      await this.registerer.register();
    } catch (err) {
      console.error("SipEngine register failed", err);
      this.emitReg("failed");
    }
  }

  private handleInvite(invitation: Invitation) {
    const peer =
      invitation.remoteIdentity.uri.user || invitation.remoteIdentity.uri.toString();
    const display = invitation.remoteIdentity.displayName || peer;
    const lineId = this.nextLineId++;
    this.lines.set(lineId, {
      session: invitation,
      direction: 2,
      peer,
      displayName: display,
      startedAt: Date.now(),
      answeredAt: null,
      clientId: `in-${lineId}-${Date.now()}`,
    });
    this.wireSession(lineId, invitation, 2, peer, display);
    this.emitCall("incoming", 2, peer, display, lineId);
    this.emitCall("ringing", 2, peer, display, lineId);
    if (this.autoPickup) {
      void this.accept(lineId);
    }
  }

  private wireSession(
    lineId: number,
    session: Session,
    direction: number,
    peer: string,
    display: string,
  ) {
    session.stateChange.addListener((state) => {
      const entry = this.lines.get(lineId);
      if (state === SessionState.Establishing) {
        this.emitCall(direction === 1 ? "calling" : "ringing", direction, peer, display, lineId);
      } else if (state === SessionState.Established) {
        if (entry) {
          entry.answeredAt = Date.now();
        }
        playRemoteAudio(session);
        this.emitCall("connected", direction, peer, display, lineId);
      } else if (state === SessionState.Terminated) {
        if (entry) {
          this.persistCallLog(entry);
        }
        this.emitCall("disconnected", direction, peer, display, lineId);
        this.lines.delete(lineId);
        if (this.activeLine === lineId) {
          const next = this.lines.keys().next();
          this.activeLine = next.done ? 1 : next.value;
        }
      }
    });
  }

  private destinationUri(raw: string): URI | undefined {
    const prefix = this.params.techprefix || "";
    const number = `${prefix}${raw}`.trim();
    const domain = this.domain();
    return UserAgent.makeURI(`sip:${number}@${domain}`) ?? undefined;
  }

  async call(destination: string, lineId?: number) {
    if (!this.userAgent) return;
    const target = this.destinationUri(destination);
    if (!target) return;
    const id = typeof lineId === "number" ? lineId : this.activeLine || this.nextLineId++;
    if (id >= this.nextLineId) this.nextLineId = id + 1;
    this.activeLine = id;
    for (const [lid] of this.lines) {
      if (lid !== id) {
        await this.hold(true, lid);
      }
    }
    const inviter = new Inviter(this.userAgent, target, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });
    this.lines.set(id, {
      session: inviter,
      direction: 1,
      peer: destination,
      displayName: destination,
      startedAt: Date.now(),
      answeredAt: null,
      clientId: `out-${id}-${Date.now()}`,
    });
    this.wireSession(id, inviter, 1, destination, destination);
    this.emitCall("calling", 1, destination, destination, id);
    try {
      await inviter.invite();
    } catch (err) {
      console.error("SipEngine invite failed", err);
      const failed = this.lines.get(id);
      if (failed) this.persistCallLog(failed);
      this.emitCall("disconnected", 1, destination, destination, id);
      this.lines.delete(id);
    }
  }

  setline(lineId: number) {
    this.activeLine = Number(lineId) || this.activeLine;
  }

  getline() {
    return this.activeLine;
  }

  private current(): LineSession | undefined {
    return this.lines.get(this.activeLine);
  }

  async accept(lineId?: number) {
    const id = lineId ?? this.activeLine;
    const entry = this.lines.get(id);
    if (!entry) return;
    this.activeLine = id;
    const session = entry.session as Invitation;
    if (typeof session.accept === "function") {
      await session.accept({
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      });
      playRemoteAudio(session);
    }
  }

  async reject(lineId?: number) {
    const id = lineId ?? this.activeLine;
    const entry = this.lines.get(id);
    if (!entry) return;
    const session = entry.session as Invitation;
    if (typeof session.reject === "function") {
      await session.reject();
    } else {
      await this.hangup(id);
    }
  }

  async hangup(lineId?: number) {
    const id = lineId ?? this.activeLine;
    const entry = this.lines.get(id);
    if (!entry) {
      for (const [lid, row] of this.lines) {
        await this.hangupSession(row.session);
        this.lines.delete(lid);
      }
      return;
    }
    await this.hangupSession(entry.session);
  }

  private async hangupSession(session: Session) {
    try {
      if (session.state === SessionState.Established) {
        await session.bye();
      } else if (session instanceof Inviter) {
        await session.cancel();
      } else if (typeof (session as Invitation).reject === "function") {
        await (session as Invitation).reject();
      } else {
        await session.dispose();
      }
    } catch (err) {
      console.error("SipEngine hangup failed", err);
      try {
        await session.dispose();
      } catch {
        /* ignore */
      }
    }
  }

  async hold(state: boolean, lineId?: number) {
    const entry = this.lines.get(lineId ?? this.activeLine);
    if (!entry) return;
    try {
      await entry.session.invite({
        sessionDescriptionHandlerModifiers: [state ? holdModifier : unholdModifier],
      });
    } catch (err) {
      console.error("SipEngine hold failed", err);
    }
  }

  mute(muted: boolean, _direction?: number) {
    this.muteState = muted;
    const entry = this.current();
    const sdh = entry?.session.sessionDescriptionHandler as {
      peerConnection?: RTCPeerConnection;
    } | null;
    const pc = sdh?.peerConnection;
    if (!pc) return this.muteState;
    pc.getSenders()
      .filter((s) => s.track?.kind === "audio")
      .forEach((s) => {
        if (s.track) s.track.enabled = !muted;
      });
    return this.muteState;
  }

  ismuted() {
    return this.muteState;
  }

  dtmf(digit: string | number) {
    const entry = this.current();
    const sdh = entry?.session.sessionDescriptionHandler as {
      peerConnection?: RTCPeerConnection;
    } | null;
    const pc = sdh?.peerConnection;
    const sender = pc?.getSenders().find((s) => s.track?.kind === "audio");
    const dtmfSender = sender?.dtmf;
    if (dtmfSender) {
      dtmfSender.insertDTMF(String(digit), 160, 70);
      return;
    }
    try {
      void entry?.session.info({
        requestOptions: {
          body: {
            contentDisposition: "render",
            contentType: "application/dtmf-relay",
            content: `Signal=${digit}\r\nDuration=160`,
          },
        },
      } as never);
    } catch (err) {
      console.error("SipEngine dtmf failed", err);
    }
  }

  getcallstate() {
    const entry = this.current();
    if (!entry) return "";
    if (entry.session.state === SessionState.Established) return "connected";
    if (entry.session.state === SessionState.Establishing) {
      return entry.direction === 1 ? "calling" : "ringing";
    }
    return "";
  }

  getpeername() {
    return this.current()?.peer || "";
  }

  getregistrationstate() {
    return this.regState;
  }

  setvolume(_spk: number, _mic: number) {
    /* browser volume is device-level */
  }

  setSpeaker(on: boolean) {
    const el = document.getElementById("papaya-remote-audio") as HTMLAudioElement | null;
    if (el) el.muted = !on;
  }

  listcallhistory() {
    try {
      return localStorage.getItem(HISTORY_KEY) || "";
    } catch {
      return "";
    }
  }

  private appendHistory(
    direction: number,
    number: string,
    name: string,
    duration: number,
    discreason: string,
  ) {
    try {
      const date = new Date().toISOString();
      const line = [direction === 1 ? "1" : "2", name, number, date, String(duration), discreason].join(
        "\t",
      );
      const prev = localStorage.getItem(HISTORY_KEY) || "";
      localStorage.setItem(HISTORY_KEY, prev ? `${prev}\r\n${line}` : line);
    } catch {
      /* ignore */
    }
  }

  private classifyDirection(entry: LineSession): CallLogDirection {
    if (entry.direction === 2 && entry.answeredAt == null) return "missed";
    if (entry.direction === 2) return "incoming";
    return "outgoing";
  }

  private persistCallLog(entry: LineSession) {
    const endedAt = Date.now();
    const answered = entry.answeredAt != null;
    const durationSeconds = answered
      ? Math.max(0, Math.round((endedAt - (entry.answeredAt as number)) / 1000))
      : 0;
    const direction = this.classifyDirection(entry);
    const startedIso = new Date(entry.startedAt).toISOString();
    const endedIso = new Date(endedAt).toISOString();

    this.appendHistory(
      entry.direction,
      entry.peer,
      entry.displayName,
      durationSeconds,
      answered ? "0" : "1",
    );

    void createCallLog({
      direction,
      number: entry.peer,
      display_name: entry.displayName || entry.peer,
      started_at: startedIso,
      ended_at: endedIso,
      duration_seconds: durationSeconds,
      answered,
      client_id: entry.clientId,
    })
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.callLogsRoot });
      })
      .catch((err) => {
        console.warn("SipEngine: failed to persist call log", err);
      });
  }

  async unregister() {
    try {
      await this.registerer?.unregister();
    } catch {
      /* ignore */
    }
    this.emitReg("unregistered");
  }

  async stop() {
    await this.stopInternal(true);
  }

  private async stopInternal(emit: boolean) {
    for (const [id] of this.lines) {
      await this.hangup(id);
    }
    this.lines.clear();
    try {
      await this.registerer?.unregister();
    } catch {
      /* ignore */
    }
    this.registerer = null;
    try {
      await this.userAgent?.stop();
    } catch {
      /* ignore */
    }
    this.userAgent = null;
    if (emit) this.emitApp("stopped");
  }
}

export const sipEngine = new SipEngine();
